import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { useToast } from '../../hooks/use-toast';
import { Inbox, Send, History, Trash2, Plus, Reply, Users, Mail, Upload, AtSign, Star, Edit, RefreshCw, PenSquare, Paperclip, Download } from 'lucide-react';
import api from '../../utils/api';

const emptyAccount = { email: '', name: '', password: '', smtp_host: '', smtp_port: 587, smtp_user: '', smtp_password: '', use_tls: true };

const EmailManagement = ({ onUpdate }) => {
  const [tab, setTab] = useState('inbox');
  const { toast } = useToast();

  // アカウント
  const [accounts, setAccounts] = useState([]);
  const [accountForm, setAccountForm] = useState(emptyAccount);
  const [editingAccount, setEditingAccount] = useState(null);
  const [accountDialogOpen, setAccountDialogOpen] = useState(false);

  // 受信箱（実メールボックス）
  const [selectedMailbox, setSelectedMailbox] = useState('');
  const [messages, setMessages] = useState([]);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [viewMail, setViewMail] = useState(null);
  const [selectedUids, setSelectedUids] = useState([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  // 作成/返信
  const [composeOpen, setComposeOpen] = useState(false);
  const [compose, setCompose] = useState({ to: '', subject: '', body: '' });
  const [composeFrom, setComposeFrom] = useState('');
  const [sending, setSending] = useState(false);

  // 配信
  const [subscribers, setSubscribers] = useState([]);
  const [newSub, setNewSub] = useState({ email: '', name: '' });
  const [bulkText, setBulkText] = useState('');
  const [newsletter, setNewsletter] = useState({ subject: '', body: '' });
  const [newsletterFrom, setNewsletterFrom] = useState('');
  const [broadcasting, setBroadcasting] = useState(false);

  // 送信履歴
  const [logs, setLogs] = useState([]);

  const mailboxAccounts = accounts.filter((a) => a.is_mailbox);

  const fetchAccounts = useCallback(async () => {
    try {
      const res = await api.get('/email/accounts');
      setAccounts(res.data);
      const mbx = res.data.filter((a) => a.is_mailbox);
      const def = res.data.find((a) => a.is_default);
      setNewsletterFrom(def ? def.id : '');
      setSelectedMailbox((prev) => prev || (mbx[0] ? mbx[0].id : ''));
    } catch (e) { console.error('アカウント取得失敗:', e); }
  }, []);

  const fetchMessages = useCallback(async (mailboxId) => {
    if (!mailboxId) { setMessages([]); return; }
    setLoadingMsgs(true);
    try {
      const res = await api.get(`/mailbox/${mailboxId}/messages`);
      setMessages(res.data);
      setSelectedUids([]);
    } catch (e) { console.error('受信メール取得失敗:', e); setMessages([]); }
    finally { setLoadingMsgs(false); }
  }, []);

  const fetchSubscribers = useCallback(async () => {
    try { const res = await api.get('/email/subscribers'); setSubscribers(res.data); } catch (e) { console.error(e); }
  }, []);
  const fetchLogs = useCallback(async () => {
    try { const res = await api.get('/email/logs'); setLogs(res.data); } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { fetchAccounts(); fetchSubscribers(); fetchLogs(); }, [fetchAccounts, fetchSubscribers, fetchLogs]);
  useEffect(() => { if (selectedMailbox) fetchMessages(selectedMailbox); }, [selectedMailbox, fetchMessages]);

  // ===== 受信箱 =====
  const openMail = async (m) => {
    try {
      const res = await api.get(`/mailbox/${selectedMailbox}/messages/${m.uid}`);
      setViewMail(res.data);
      setViewOpen(true);
      if (!m.seen) {
        await api.post(`/mailbox/${selectedMailbox}/messages/${m.uid}/read`);
        setMessages((prev) => prev.map((x) => (x.uid === m.uid ? { ...x, seen: true } : x)));
        if (onUpdate) onUpdate();
      }
    } catch (e) {
      toast({ title: 'エラー', description: 'メールを開けませんでした', variant: 'destructive' });
    }
  };

  const deleteMail = async (uid) => {
    if (!window.confirm('このメールを削除しますか？')) return;
    try {
      await api.delete(`/mailbox/${selectedMailbox}/messages/${uid}`);
      toast({ title: '削除完了', description: 'メールを削除しました' });
      setViewOpen(false);
      fetchMessages(selectedMailbox);
    } catch (e) { toast({ title: 'エラー', description: '削除に失敗しました', variant: 'destructive' }); }
  };

  const downloadAttachment = async (uid, att) => {
    try {
      const res = await api.get(`/mailbox/${selectedMailbox}/messages/${uid}/attachments/${att.index}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: att.content_type }));
      const a = document.createElement('a');
      a.href = url; a.download = att.filename; document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast({ title: 'エラー', description: 'ダウンロードに失敗しました', variant: 'destructive' }); }
  };

  const toggleSelect = (uid) => {
    setSelectedUids((prev) => (prev.includes(uid) ? prev.filter((x) => x !== uid) : [...prev, uid]));
  };
  const toggleSelectAll = () => {
    setSelectedUids((prev) => (prev.length === messages.length ? [] : messages.map((m) => m.uid)));
  };
  const bulkDelete = async () => {
    if (selectedUids.length === 0) return;
    if (!window.confirm(`選択した ${selectedUids.length} 件のメールを削除しますか？`)) return;
    setBulkDeleting(true);
    try {
      await Promise.all(selectedUids.map((uid) => api.delete(`/mailbox/${selectedMailbox}/messages/${uid}`)));
      toast({ title: '削除完了', description: `${selectedUids.length}件のメールを削除しました` });
      setSelectedUids([]);
      fetchMessages(selectedMailbox);
    } catch (e) {
      toast({ title: 'エラー', description: '一部の削除に失敗しました', variant: 'destructive' });
      fetchMessages(selectedMailbox);
    } finally { setBulkDeleting(false); }
  };

  const openCompose = (to = '', subject = '') => {
    setCompose({ to, subject, body: '' });
    setComposeFrom(selectedMailbox || (accounts.find((a) => a.is_default) || {}).id || '');
    setComposeOpen(true);
  };
  const openReply = () => {
    if (!viewMail) return;
    openCompose(viewMail.from, viewMail.subject?.startsWith('Re:') ? viewMail.subject : `Re: ${viewMail.subject || ''}`);
    setCompose((c) => ({ ...c, body: `\n\n----- 元のメッセージ -----\n${viewMail.body || ''}` }));
    setViewOpen(false);
  };

  const handleSend = async () => {
    if (!compose.to.trim() || !compose.subject.trim() || !compose.body.trim()) {
      toast({ title: 'エラー', description: '宛先・件名・本文を入力してください', variant: 'destructive' });
      return;
    }
    setSending(true);
    try {
      const res = await api.post('/email/reply', { to_email: compose.to, subject: compose.subject, body: compose.body, account_id: composeFrom || null });
      if (res.data.success) {
        toast({ title: '送信完了', description: `${compose.to} へ送信しました` });
        setComposeOpen(false);
        fetchLogs();
      } else {
        toast({ title: '送信失敗', description: res.data.error || '送信できませんでした', variant: 'destructive' });
        fetchLogs();
      }
    } catch (e) {
      toast({ title: 'エラー', description: e.response?.data?.detail || '送信に失敗しました', variant: 'destructive' });
    } finally { setSending(false); }
  };

  // ===== 配信 =====
  const handleAddSubscriber = async (e) => {
    e.preventDefault();
    if (!newSub.email.trim()) return;
    try {
      await api.post('/email/subscribers', { email: newSub.email, name: newSub.name || null });
      toast({ title: '登録完了', description: '配信登録者を追加しました' });
      setNewSub({ email: '', name: '' }); fetchSubscribers();
    } catch (e) { toast({ title: 'エラー', description: e.response?.data?.detail || '追加に失敗しました', variant: 'destructive' }); }
  };
  const handleBulkImport = async () => {
    const emails = bulkText.split(/[\n,;\s]+/).map((s) => s.trim()).filter(Boolean);
    if (emails.length === 0) return;
    try {
      const res = await api.post('/email/subscribers/bulk', { emails });
      toast({ title: 'インポート完了', description: `${res.data.added}件追加 / ${res.data.skipped}件スキップ` });
      setBulkText(''); fetchSubscribers();
    } catch (e) { toast({ title: 'エラー', description: 'インポートに失敗しました', variant: 'destructive' }); }
  };
  const handleToggleSub = async (id) => { try { await api.patch(`/email/subscribers/${id}/toggle`); fetchSubscribers(); } catch (_) {} };
  const handleDeleteSub = async (id) => {
    if (!window.confirm('この登録者を削除しますか？')) return;
    try { await api.delete(`/email/subscribers/${id}`); fetchSubscribers(); } catch (_) {}
  };
  const handleSendNewsletter = async () => {
    if (!newsletter.subject.trim() || !newsletter.body.trim()) { toast({ title: 'エラー', description: '件名と本文を入力してください', variant: 'destructive' }); return; }
    const activeCount = subscribers.filter((s) => s.is_active).length;
    if (activeCount === 0) { toast({ title: 'エラー', description: '有効な配信登録者がいません', variant: 'destructive' }); return; }
    if (!window.confirm(`有効な登録者 ${activeCount}件へ一斉配信します。よろしいですか？`)) return;
    setBroadcasting(true);
    try {
      const res = await api.post('/email/newsletter', { subject: newsletter.subject, body: newsletter.body, account_id: newsletterFrom || null });
      toast({ title: res.data.success ? '配信完了' : '一部配信できませんでした', description: `送信 ${res.data.sent}件 / 失敗 ${res.data.failed}件（対象 ${res.data.total}件）`, variant: res.data.success ? 'default' : 'destructive' });
      setNewsletter({ subject: '', body: '' }); fetchLogs();
    } catch (e) { toast({ title: 'エラー', description: e.response?.data?.detail || '配信に失敗しました', variant: 'destructive' }); }
    finally { setBroadcasting(false); }
  };

  // ===== アカウント =====
  const openNewAccount = () => { setEditingAccount(null); setAccountForm(emptyAccount); setAccountDialogOpen(true); };
  const openEditAccount = (a) => {
    setEditingAccount(a);
    setAccountForm({ email: a.email, name: a.name || '', password: '', smtp_host: a.smtp_host || '', smtp_port: a.smtp_port || 587, smtp_user: a.smtp_user || '', smtp_password: '', use_tls: a.use_tls !== false });
    setAccountDialogOpen(true);
  };
  const handleSaveAccount = async (e) => {
    e.preventDefault();
    if (!accountForm.email.trim()) return;
    const payload = { ...accountForm, smtp_port: accountForm.smtp_port ? Number(accountForm.smtp_port) : null };
    try {
      if (editingAccount) { await api.put(`/email/accounts/${editingAccount.id}`, payload); toast({ title: '更新完了', description: 'アカウントを更新しました' }); }
      else { await api.post('/email/accounts', payload); toast({ title: '作成完了', description: 'メールアカウントを作成しました' }); }
      setAccountDialogOpen(false); fetchAccounts();
    } catch (err) { toast({ title: 'エラー', description: err.response?.data?.detail || '保存に失敗しました', variant: 'destructive' }); }
  };
  const handleSetDefault = async (id) => { try { await api.patch(`/email/accounts/${id}/default`); fetchAccounts(); } catch (_) {} };
  const handleDeleteAccount = async (id) => {
    if (!window.confirm('このアカウント（メールボックス）を削除しますか？受信済みメールも削除されます。')) return;
    try { await api.delete(`/email/accounts/${id}`); toast({ title: '削除完了', description: 'アカウントを削除しました' }); fetchAccounts(); } catch (_) {}
  };

  const unreadCount = messages.filter((m) => !m.seen).length;

  const statusBadge = (s) => {
    const map = { sent: 'bg-green-500/20 text-green-400 border-green-500/30', failed: 'bg-red-500/20 text-red-400 border-red-500/30', partial: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' };
    const label = { sent: '送信済み', failed: '失敗', partial: '一部送信' };
    return <span className={`px-2 py-0.5 text-xs rounded-full border ${map[s] || map.failed}`}>{label[s] || s}</span>;
  };

  const FromSelector = ({ value, onChange }) => (
    <div className="space-y-2">
      <Label className="text-gray-600">送信元</Label>
      <Select value={value || 'local'} onValueChange={(v) => onChange(v === 'local' ? '' : v)}>
        <SelectTrigger data-testid="from-account-select" className="bg-white/50 border-amber-500/30 text-gray-900"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="local">サーバー内メール (既定)</SelectItem>
          {accounts.map((a) => (<SelectItem key={a.id} value={a.id}>{a.name ? `${a.name} <${a.email}>` : a.email}</SelectItem>))}
        </SelectContent>
      </Select>
    </div>
  );

  return (
    <Card className="bg-gradient-to-br from-gray-50/90 to-gray-100/90 border-amber-500/30 backdrop-blur-sm" data-testid="email-management">
      <CardHeader>
        <CardTitle className="text-xl sm:text-2xl bg-gradient-to-r from-amber-500 to-amber-600 bg-clip-text text-transparent flex items-center gap-2">
          <Mail className="w-6 h-6 text-amber-500" />メール管理
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList className="grid grid-cols-4 w-full bg-white/50 border border-amber-500/30 mb-6">
            <TabsTrigger value="inbox" data-testid="email-tab-inbox" className="data-[state=active]:bg-amber-500/20 data-[state=active]:text-amber-500 text-xs sm:text-sm">
              <Inbox className="w-4 h-4 mr-1 hidden sm:inline" />受信箱{unreadCount > 0 && <span className="ml-1 px-1.5 py-0.5 text-[10px] rounded-full bg-amber-500 text-black font-bold">{unreadCount}</span>}
            </TabsTrigger>
            <TabsTrigger value="newsletter" data-testid="email-tab-newsletter" className="data-[state=active]:bg-amber-500/20 data-[state=active]:text-amber-500 text-xs sm:text-sm"><Send className="w-4 h-4 mr-1 hidden sm:inline" />一斉配信</TabsTrigger>
            <TabsTrigger value="accounts" data-testid="email-tab-accounts" className="data-[state=active]:bg-amber-500/20 data-[state=active]:text-amber-500 text-xs sm:text-sm"><AtSign className="w-4 h-4 mr-1 hidden sm:inline" />アカウント</TabsTrigger>
            <TabsTrigger value="logs" data-testid="email-tab-logs" className="data-[state=active]:bg-amber-500/20 data-[state=active]:text-amber-500 text-xs sm:text-sm"><History className="w-4 h-4 mr-1 hidden sm:inline" />送信履歴</TabsTrigger>
          </TabsList>

          {/* 受信箱 */}
          <TabsContent value="inbox">
            {mailboxAccounts.length === 0 ? (
              <div className="text-center py-10">
                <p className="text-gray-600 mb-3">メールアドレスがまだありません。</p>
                <p className="text-sm text-gray-500">「アカウント」タブで <span className="text-amber-500">〇〇@presage.co.jp</span> を作成すると、ここで受信メールを確認できます。</p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                  <Select value={selectedMailbox} onValueChange={setSelectedMailbox}>
                    <SelectTrigger data-testid="mailbox-select" className="bg-white/50 border-amber-500/30 text-gray-900 sm:w-72"><SelectValue placeholder="メールボックスを選択" /></SelectTrigger>
                    <SelectContent>
                      {mailboxAccounts.map((a) => (<SelectItem key={a.id} value={a.id}>{a.email}</SelectItem>))}
                    </SelectContent>
                  </Select>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => fetchMessages(selectedMailbox)} data-testid="mailbox-refresh-btn" className="border-amber-500/30 text-amber-500 hover:bg-amber-500/10"><RefreshCw className="w-4 h-4 mr-1" />更新</Button>
                    <Button size="sm" onClick={() => openCompose()} data-testid="mail-compose-btn" className="bg-gradient-to-r from-amber-500 to-amber-600"><PenSquare className="w-4 h-4 mr-1" />新規作成</Button>
                  </div>
                </div>
                {messages.length > 0 && (
                  <div className="flex items-center gap-3 px-1">
                    <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                      <input type="checkbox" data-testid="mail-select-all" checked={selectedUids.length === messages.length && messages.length > 0} onChange={toggleSelectAll} className="accent-amber-500 w-4 h-4" />
                      全選択
                    </label>
                    {selectedUids.length > 0 && (
                      <Button size="sm" variant="outline" onClick={bulkDelete} disabled={bulkDeleting} data-testid="mail-bulk-delete-btn" className="border-red-500/40 text-red-400 hover:bg-red-500/10">
                        <Trash2 className="w-4 h-4 mr-1" />{bulkDeleting ? '削除中...' : `選択削除 (${selectedUids.length})`}
                      </Button>
                    )}
                  </div>
                )}
                <div className="space-y-2">
                  {loadingMsgs ? (
                    <p className="text-gray-500 text-center py-8">読み込み中...</p>
                  ) : messages.length === 0 ? (
                    <p className="text-gray-500 text-center py-8">受信メールがありません</p>
                  ) : (
                    messages.map((m) => (
                      <div key={m.uid} onClick={() => openMail(m)} data-testid={`mail-item-${m.uid}`}
                        className={`p-4 rounded-lg border cursor-pointer transition-all flex items-start justify-between gap-4 ${m.seen ? 'bg-white/40 border-amber-500/10 hover:border-amber-500/30' : 'bg-amber-500/5 border-amber-500/40 hover:border-amber-500/60'}`}>
                        <div className="flex items-start gap-3 min-w-0">
                          <input type="checkbox" data-testid={`mail-select-${m.uid}`} checked={selectedUids.includes(m.uid)} onClick={(e) => e.stopPropagation()} onChange={() => toggleSelect(m.uid)} className="accent-amber-500 w-4 h-4 mt-1 flex-shrink-0" />
                          {!m.seen && <span className="w-2.5 h-2.5 rounded-full bg-amber-500 mt-2 flex-shrink-0" />}
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className={`${m.seen ? 'text-gray-600' : 'text-gray-900 font-semibold'}`}>{m.from_name || m.from}</span>
                              <span className="text-xs text-amber-500 truncate">{m.from}</span>
                            </div>
                            <p className={`text-sm mt-1 ${m.seen ? 'text-gray-600' : 'text-gray-900'}`}>{m.subject || '(件名なし)'}</p>
                            <p className="text-xs text-gray-500 truncate mt-0.5">{m.snippet}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <span className="text-xs text-gray-600">{m.date ? new Date(m.date).toLocaleString('ja-JP') : ''}</span>
                          <button onClick={(e) => { e.stopPropagation(); deleteMail(m.uid); }} data-testid={`mail-delete-list-${m.uid}`} title="削除" className="text-red-400 hover:text-red-300 p-1 rounded hover:bg-red-500/10">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </TabsContent>

          {/* 一斉配信 */}
          <TabsContent value="newsletter">
            <div className="grid lg:grid-cols-2 gap-6">
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2"><Users className="w-5 h-5 text-amber-500" />配信登録者（{subscribers.filter((s) => s.is_active).length}/{subscribers.length}）</h3>
                <form onSubmit={handleAddSubscriber} className="flex flex-col sm:flex-row gap-2">
                  <Input type="email" placeholder="メールアドレス" value={newSub.email} onChange={(e) => setNewSub({ ...newSub, email: e.target.value })} data-testid="subscriber-email-input" className="bg-white/50 border-amber-500/30 text-gray-900" required />
                  <Input placeholder="名前(任意)" value={newSub.name} onChange={(e) => setNewSub({ ...newSub, name: e.target.value })} className="bg-white/50 border-amber-500/30 text-gray-900 sm:w-32" />
                  <Button type="submit" data-testid="subscriber-add-btn" className="bg-gradient-to-r from-amber-500 to-amber-600 flex-shrink-0"><Plus className="w-4 h-4" /></Button>
                </form>
                <div className="space-y-2">
                  <Label className="text-gray-600 text-sm">一括インポート（改行/カンマ区切り）</Label>
                  <Textarea value={bulkText} onChange={(e) => setBulkText(e.target.value)} rows={3} placeholder="a@example.com&#10;b@example.com" data-testid="subscriber-bulk-input" className="bg-white/50 border-amber-500/30 text-gray-900" />
                  <Button onClick={handleBulkImport} variant="outline" size="sm" data-testid="subscriber-bulk-btn" className="border-amber-500/30 text-amber-500 hover:bg-amber-500/10"><Upload className="w-4 h-4 mr-1" />インポート</Button>
                </div>
                <div className="max-h-64 overflow-y-auto space-y-2 pr-1">
                  {subscribers.map((s) => (
                    <div key={s.id} data-testid={`subscriber-${s.id}`} className="flex items-center justify-between p-2 rounded bg-white/50 border border-amber-500/10">
                      <div className="min-w-0"><p className="text-sm text-gray-900 truncate">{s.email}</p>{s.name && <p className="text-xs text-gray-500">{s.name}</p>}</div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <button onClick={() => handleToggleSub(s.id)} className={`px-2 py-0.5 text-xs rounded-full border ${s.is_active ? 'bg-green-500/20 text-green-400 border-green-500/30' : 'bg-gray-100/20 text-gray-600 border-gray-300/30'}`}>{s.is_active ? '有効' : '無効'}</button>
                        <button onClick={() => handleDeleteSub(s.id)} className="text-red-400 hover:text-red-300"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2"><Send className="w-5 h-5 text-amber-500" />お知らせを配信</h3>
                <FromSelector value={newsletterFrom} onChange={setNewsletterFrom} />
                <div className="space-y-2"><Label className="text-gray-600">件名</Label><Input value={newsletter.subject} onChange={(e) => setNewsletter({ ...newsletter, subject: e.target.value })} data-testid="newsletter-subject-input" className="bg-white/50 border-amber-500/30 text-gray-900" /></div>
                <div className="space-y-2"><Label className="text-gray-600">本文</Label><Textarea value={newsletter.body} onChange={(e) => setNewsletter({ ...newsletter, body: e.target.value })} rows={8} data-testid="newsletter-body-input" className="bg-white/50 border-amber-500/30 text-gray-900" /></div>
                <Button onClick={handleSendNewsletter} disabled={broadcasting} data-testid="newsletter-send-btn" className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700"><Send className="w-4 h-4 mr-2" />{broadcasting ? '配信中...' : '一斉配信する'}</Button>
              </div>
            </div>
          </TabsContent>

          {/* アカウント */}
          <TabsContent value="accounts">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2"><AtSign className="w-5 h-5 text-amber-500" />メールアカウント</h3>
                <Button onClick={openNewAccount} data-testid="account-add-btn" className="bg-gradient-to-r from-amber-500 to-amber-600"><Plus className="w-4 h-4 mr-1" />新規追加</Button>
              </div>
              <p className="text-sm text-gray-500">@presage.co.jp のアドレスを作成すると、自社サーバー上に実際のメールボックスが作られ、送受信できます（受信箱タブで確認）。外部SMTPを指定した場合は送信専用アカウントになります。</p>
              {accounts.length === 0 ? (
                <p className="text-gray-500 text-center py-8">アカウントがありません</p>
              ) : (
                <div className="space-y-3">
                  {accounts.map((a) => (
                    <div key={a.id} data-testid={`account-${a.id}`} className="p-4 rounded-lg bg-white/50 border border-amber-500/10 flex items-start justify-between gap-4">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-gray-900 font-medium">{a.name || a.email}</span>
                          {a.is_default && <span className="px-2 py-0.5 text-xs rounded-full bg-amber-500/20 text-amber-500 border border-amber-500/30 flex items-center gap-1"><Star className="w-3 h-3" />既定</span>}
                          {a.is_mailbox ? <span className="px-2 py-0.5 text-xs rounded-full bg-green-500/20 text-green-400 border border-green-500/30">メールボックス</span> : <span className="px-2 py-0.5 text-xs rounded-full bg-amber-600/20 text-amber-500 border border-amber-600/30">送信専用</span>}
                        </div>
                        <p className="text-sm text-amber-500 mt-1">{a.email}</p>
                        <p className="text-xs text-gray-500 mt-1">{a.is_mailbox ? (a.provisioned === false ? '⚠️ サーバー未反映（本番のみ有効）' : '自社サーバーで送受信') : (a.smtp_host ? `外部SMTP: ${a.smtp_host}:${a.smtp_port}` : 'サーバー内メールで送信')}</p>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {!a.is_default && <Button size="sm" variant="outline" onClick={() => handleSetDefault(a.id)} data-testid={`account-default-${a.id}`} className="border-amber-500/30 text-amber-500 hover:bg-amber-500/10"><Star className="w-4 h-4" /></Button>}
                        <Button size="sm" variant="outline" onClick={() => openEditAccount(a)} data-testid={`account-edit-${a.id}`} className="border-amber-500/30 text-amber-500 hover:bg-amber-500/10"><Edit className="w-4 h-4" /></Button>
                        <Button size="sm" variant="outline" onClick={() => handleDeleteAccount(a.id)} data-testid={`account-delete-${a.id}`} className="border-red-500/30 text-red-400 hover:bg-red-500/10"><Trash2 className="w-4 h-4" /></Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* 送信履歴 */}
          <TabsContent value="logs">
            <div className="space-y-3">
              {logs.length === 0 ? (<p className="text-gray-500 text-center py-8">送信履歴がありません</p>) : (
                logs.map((log) => (
                  <div key={log.id} data-testid={`log-item-${log.id}`} className="p-4 rounded-lg bg-white/50 border border-amber-500/10">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-0.5 text-xs rounded-full border ${log.type === 'newsletter' ? 'bg-purple-500/20 text-purple-400 border-purple-500/30' : 'bg-amber-600/20 text-amber-500 border-amber-600/30'}`}>{log.type === 'newsletter' ? '一斉配信' : '送信'}</span>
                        {statusBadge(log.status)}
                      </div>
                      <span className="text-xs text-gray-600">{new Date(log.created_at).toLocaleString('ja-JP')}</span>
                    </div>
                    <p className="text-gray-900 font-medium mt-2">{log.subject}</p>
                    <p className="text-sm text-gray-600 mt-1">宛先: {log.to}{log.type === 'newsletter' && ` （送信 ${log.sent_count} / 失敗 ${log.failed_count}）`}</p>
                    {log.error && <p className="text-xs text-red-400 mt-1 truncate">{log.error}</p>}
                  </div>
                ))
              )}
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>

      {/* メール閲覧ダイアログ */}
      <Dialog open={viewOpen} onOpenChange={setViewOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white border-amber-500/30 text-gray-900">
          <DialogHeader>
            <DialogTitle className="text-xl bg-gradient-to-r from-amber-500 to-amber-600 bg-clip-text text-transparent">{viewMail?.subject || '(件名なし)'}</DialogTitle>
            <DialogDescription className="text-gray-600">差出人: {viewMail?.from_name ? `${viewMail.from_name} <${viewMail.from}>` : viewMail?.from} ／ {viewMail?.date ? new Date(viewMail.date).toLocaleString('ja-JP') : ''}</DialogDescription>
          </DialogHeader>
          {viewMail && (
            <div className="space-y-4 mt-2">
              <div className="bg-white/50 p-4 rounded-lg border border-amber-500/10 max-h-96 overflow-y-auto">
                <p className="text-sm text-gray-700 whitespace-pre-wrap">{viewMail.body}</p>
              </div>
              {viewMail.attachments && viewMail.attachments.length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm text-gray-600 flex items-center gap-1"><Paperclip className="w-4 h-4" />添付ファイル ({viewMail.attachments.length})</p>
                  {viewMail.attachments.map((att) => (
                    <button key={att.index} type="button" onClick={() => downloadAttachment(viewMail.uid, att)} data-testid={`attachment-${att.index}`}
                      className="w-full flex items-center justify-between gap-3 p-3 rounded-lg bg-white/60 border border-amber-500/20 hover:border-amber-500/50 hover:bg-amber-500/5 transition-all text-left">
                      <span className="flex items-center gap-2 min-w-0">
                        <Paperclip className="w-4 h-4 text-amber-500 flex-shrink-0" />
                        <span className="text-sm text-gray-900 truncate">{att.filename}</span>
                      </span>
                      <span className="flex items-center gap-2 flex-shrink-0 text-xs text-amber-500">
                        {(att.size / 1024).toFixed(1)} KB
                        <Download className="w-4 h-4" />
                      </span>
                    </button>
                  ))}
                </div>
              )}
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => deleteMail(viewMail.uid)} data-testid="mail-delete-btn" className="border-red-500/30 text-red-400 hover:bg-red-500/10"><Trash2 className="w-4 h-4 mr-1" />削除</Button>
                <Button onClick={openReply} data-testid="mail-reply-btn" className="bg-gradient-to-r from-amber-500 to-amber-600"><Reply className="w-4 h-4 mr-1" />返信</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* 作成/返信ダイアログ */}
      <Dialog open={composeOpen} onOpenChange={setComposeOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white border-amber-500/30 text-gray-900">
          <DialogHeader>
            <DialogTitle className="text-2xl bg-gradient-to-r from-amber-500 to-amber-600 bg-clip-text text-transparent">メール作成</DialogTitle>
            <DialogDescription className="text-gray-600">自社サーバーから送信します</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <FromSelector value={composeFrom} onChange={setComposeFrom} />
            <div className="space-y-2"><Label className="text-gray-600">宛先</Label><Input type="email" value={compose.to} onChange={(e) => setCompose({ ...compose, to: e.target.value })} data-testid="compose-to-input" className="bg-white/50 border-amber-500/30 text-gray-900" /></div>
            <div className="space-y-2"><Label className="text-gray-600">件名</Label><Input value={compose.subject} onChange={(e) => setCompose({ ...compose, subject: e.target.value })} data-testid="compose-subject-input" className="bg-white/50 border-amber-500/30 text-gray-900" /></div>
            <div className="space-y-2"><Label className="text-gray-600">本文</Label><Textarea value={compose.body} onChange={(e) => setCompose({ ...compose, body: e.target.value })} rows={8} data-testid="compose-body-input" className="bg-white/50 border-amber-500/30 text-gray-900" /></div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setComposeOpen(false)} className="border-amber-500/30 text-amber-500 hover:bg-amber-500/10">キャンセル</Button>
              <Button onClick={handleSend} disabled={sending} data-testid="compose-send-btn" className="bg-gradient-to-r from-amber-500 to-amber-600"><Send className="w-4 h-4 mr-2" />{sending ? '送信中...' : '送信'}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* アカウント追加/編集ダイアログ */}
      <Dialog open={accountDialogOpen} onOpenChange={setAccountDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto bg-white border-amber-500/30 text-gray-900">
          <DialogHeader>
            <DialogTitle className="text-2xl bg-gradient-to-r from-amber-500 to-amber-600 bg-clip-text text-transparent">{editingAccount ? 'アカウントを編集' : '新規メールアカウント'}</DialogTitle>
            <DialogDescription className="text-gray-600">@presage.co.jp なら実メールボックスを作成、外部SMTP指定なら送信専用になります</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSaveAccount} className="space-y-4 mt-2">
            <div className="space-y-2"><Label className="text-gray-600">メールアドレス *</Label><Input type="email" value={accountForm.email} onChange={(e) => setAccountForm({ ...accountForm, email: e.target.value })} placeholder="info@presage.co.jp" data-testid="account-email-input" className="bg-white/50 border-amber-500/30 text-gray-900" required disabled={!!editingAccount} /></div>
            <div className="space-y-2"><Label className="text-gray-600">表示名（任意）</Label><Input value={accountForm.name} onChange={(e) => setAccountForm({ ...accountForm, name: e.target.value })} data-testid="account-name-input" className="bg-white/50 border-amber-500/30 text-gray-900" /></div>
            <div className="space-y-2"><Label className="text-gray-600">メールボックスのパスワード{editingAccount && '（変更時のみ）'}</Label><Input type="password" value={accountForm.password} onChange={(e) => setAccountForm({ ...accountForm, password: e.target.value })} placeholder="スマホ/メールソフトのログイン用" data-testid="account-password-input" className="bg-white/50 border-amber-500/30 text-gray-900" /></div>
            <details className="border-t border-amber-500/20 pt-3">
              <summary className="text-sm text-gray-600 cursor-pointer">外部SMTP設定（送信専用アカウントにする場合）</summary>
              <div className="mt-3 space-y-2">
                <div className="grid grid-cols-3 gap-2">
                  <div className="col-span-2 space-y-2"><Label className="text-gray-600 text-sm">SMTPホスト</Label><Input value={accountForm.smtp_host} onChange={(e) => setAccountForm({ ...accountForm, smtp_host: e.target.value })} data-testid="account-smtp-host-input" className="bg-white/50 border-amber-500/30 text-gray-900" /></div>
                  <div className="space-y-2"><Label className="text-gray-600 text-sm">ポート</Label><Input type="number" value={accountForm.smtp_port} onChange={(e) => setAccountForm({ ...accountForm, smtp_port: e.target.value })} data-testid="account-smtp-port-input" className="bg-white/50 border-amber-500/30 text-gray-900" /></div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-2"><Label className="text-gray-600 text-sm">ユーザー名</Label><Input value={accountForm.smtp_user} onChange={(e) => setAccountForm({ ...accountForm, smtp_user: e.target.value })} data-testid="account-smtp-user-input" className="bg-white/50 border-amber-500/30 text-gray-900" /></div>
                  <div className="space-y-2"><Label className="text-gray-600 text-sm">パスワード</Label><Input type="password" value={accountForm.smtp_password} onChange={(e) => setAccountForm({ ...accountForm, smtp_password: e.target.value })} data-testid="account-smtp-password-input" className="bg-white/50 border-amber-500/30 text-gray-900" /></div>
                </div>
                <label className="flex items-center gap-2 mt-2 text-sm text-gray-600 cursor-pointer"><input type="checkbox" checked={accountForm.use_tls} onChange={(e) => setAccountForm({ ...accountForm, use_tls: e.target.checked })} data-testid="account-tls-checkbox" className="accent-amber-500" />TLS/STARTTLS を使用</label>
              </div>
            </details>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setAccountDialogOpen(false)} className="border-amber-500/30 text-amber-500 hover:bg-amber-500/10">キャンセル</Button>
              <Button type="submit" data-testid="account-save-btn" className="bg-gradient-to-r from-amber-500 to-amber-600">{editingAccount ? '更新' : '作成'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default EmailManagement;
